import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  fetchEngagements,
  readLocalLikes,
  toggleLike as postToggleLike,
  writeLocalLike,
} from "../utils/engagementClient";

// Tüm ürün beğeni sayıları TEK istekte burada tutulur. Kartlar kendi
// başlarına istek atmaz — yüzlerce ürün için yüzlerce istek olmasın.
//
// Merkezi API (portal.mobresqr.com) hangi özelliklerin açık olduğunu da aynı
// yanıtta bildirir; kapalı özelliğin arayüzü hiç çizilmez.
const StatsContext = createContext(null);

export const StatsProvider = ({ id, children }) => {
  // { [URUN_KOD]: { likes, reviewCount, averageRating, reviews } }
  const [products, setProducts] = useState({});
  // { [URUN_KOD]: true } — bu cihazın beğendikleri (kaynak: localStorage)
  const [liked, setLiked] = useState({});
  // Sunucudan gelen özellik bayrakları. Yanıt gelene kadar hiçbir etkileşim
  // arayüzü gösterilmez; böylece kapalı bir özellik bir an için görünüp
  // kaybolmaz.
  const [settings, setSettings] = useState(null);
  // Aynı ürüne üst üste tıklamada uçuşan istek varsa yenisini engelleriz.
  const pendingRef = useRef(new Set());

  // Cihazdaki beğeniler anında yüklenir; sunucu beklenmez, kalp yanıp sönmez.
  useEffect(() => {
    if (!id) return;
    setLiked(readLocalLikes(id));
  }, [id]);

  // Sayılar ve ayarlar tek istekte gelir.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    fetchEngagements(id).then((data) => {
      if (cancelled) return;
      setProducts(data.products || {});
      setSettings(data.settings);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const toggleLike = useCallback(
    async (productId) => {
      if (!id || !productId) return;

      // Hızlı ardışık tıklamalar tek isteğe indirgenir.
      if (pendingRef.current.has(productId)) return;
      pendingRef.current.add(productId);

      const wasLiked = !!liked[productId];
      const nextLiked = !wasLiked;
      const previous = products[productId] || {};
      const previousCount = previous.likes || 0;

      // İyimser güncelleme: kullanıcı ağı beklemez.
      setLiked((current) => {
        const next = { ...current };
        if (nextLiked) next[productId] = true;
        else delete next[productId];
        return next;
      });
      setProducts((current) => ({
        ...current,
        [productId]: {
          ...previous,
          likes: Math.max(0, previousCount + (nextLiked ? 1 : -1)),
        },
      }));
      writeLocalLike(id, productId, nextLiked);

      try {
        const server = await postToggleLike(id, productId);
        // Gerçek toplam sunucudan gelir — başkalarının beğenileri de dahil.
        setProducts((current) => ({
          ...current,
          [productId]: { ...(current[productId] || {}), likes: server.likes },
        }));
        // Sunucu son sözü söyler: iki cihazda aynı anda basıldıysa ya da
        // kayıt zaten varsa, gerçek durum sunucununkidir.
        setLiked((current) => {
          const next = { ...current };
          if (server.liked) next[productId] = true;
          else delete next[productId];
          return next;
        });
        writeLocalLike(id, productId, server.liked);
      } catch {
        // Rollback: API başarısızsa eski duruma dön.
        setLiked((current) => {
          const next = { ...current };
          if (wasLiked) next[productId] = true;
          else delete next[productId];
          return next;
        });
        setProducts((current) => ({
          ...current,
          [productId]: { ...previous, likes: previousCount },
        }));
        writeLocalLike(id, productId, wasLiked);
      } finally {
        pendingRef.current.delete(productId);
      }
    },
    [id, liked, products]
  );

  return (
    <StatsContext.Provider
      value={{ restaurantId: id, products, liked, toggleLike, settings }}
    >
      {children}
    </StatsContext.Provider>
  );
};

// Provider dışında da güvenle çağrılabilir (null döner) — beğeni katmanı
// yokken menü çalışmaya devam etsin.
export const useStats = () => useContext(StatsContext);

/**
 * Bir ürünün beğeni durumu.
 *
 * `enabled`, üç koşulun tümü sağlandığında true olur: provider var, ayarlar
 * sunucudan geldi ve beğeni özelliği açık. LikeButton buna bakıp kendini hiç
 * çizmez — kapalı özellik bir an bile görünmez.
 */
export const useProductLikes = (productId) => {
  const ctx = useContext(StatsContext);
  if (!ctx) return { likes: 0, liked: false, toggleLike: null, enabled: false };
  return {
    likes: ctx.products[productId]?.likes || 0,
    liked: !!ctx.liked[productId],
    toggleLike: ctx.toggleLike,
    enabled: ctx.settings?.likes === true,
  };
};

/** Bir ürünün onaylanmış yorumları. Yorum özelliği kapalıysa boş döner. */
export const useProductReviews = (productId) => {
  const ctx = useContext(StatsContext);
  if (!ctx || ctx.settings?.reviews !== true) {
    return { reviews: [], reviewCount: 0, averageRating: 0, enabled: false };
  }
  const item = ctx.products[productId] || {};
  return {
    reviews: item.reviews || [],
    reviewCount: item.reviewCount || 0,
    averageRating: item.averageRating || 0,
    enabled: true,
  };
};

export default StatsContext;
